/**
 * Client half of the T.F2 venue-link writes (contract 22.5 since v1.3) and of
 * the additive user directory (25.5) the manager picker reads.
 *
 *   POST   /api/venues/:venueId/links          ADMIN  create or re-point
 *   PATCH  /api/venues/:venueId/links/:linkId  ADMIN  partial edit
 *   DELETE /api/venues/:venueId/links/:linkId  ADMIN  remove
 *   GET    /api/users/directory                VIEWER {id, email, displayName}
 *
 * Shapes mirror `apps/api/src/venue-links/dto.ts` (the same 1:1 rule `./venues`
 * documents — the generated `api-types.ts` is not re-exported through the
 * shared barrel). There is no link READ here on purpose: #7 already fans the
 * venue's links out with `id`/`format` on every row, so this module only
 * writes, and the card keeps ONE source for the table it renders.
 *
 * The writes are OPTIMISTIC over the venue-detail cache: the row appears, moves
 * or disappears the moment the request is sent, and `onMutate`'s snapshot puts
 * it back when the api refuses (409 on a clashing url/slot, 404 for a link that
 * was never this venue's, a dropped session). The server's answer replaces the
 * optimistic row when it arrives, because it is the truth the api normalises
 * (POST re-points by url, so the row the client meant to add may be another
 * row's id).
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { requestEnvelope } from './client';
import { venueDetailQueryKey, type VenueCourseFormat } from './venues';
import type { ManagerDepartment } from '../departments';

/** Mirrors `VenueLinkDto` — one row of `link_registry` as a write answers it. */
export interface VenueLinkDto {
  id: number;
  venueId: number;
  courseId: number;
  placeId: number;
  url: string;
  format: VenueCourseFormat | null;
}

/** POST body — the upsert (url first, then the (courseId, placeId) slot). */
export interface VenueLinkCreate {
  courseId: number;
  placeId: number;
  url: string;
  format?: VenueCourseFormat | null;
}

/** PATCH body — only provided keys change; `format: null` clears it. */
export interface VenueLinkPatch {
  courseId?: number;
  placeId?: number;
  url?: string;
  format?: VenueCourseFormat | null;
}

/**
 * Mirrors `UserDirectoryEntryDto` — the picker-grade identity (25.5, VIEWER).
 * ADDITIVE (v1.8.7, T.Z42): `department` rides the entry — the venues roster and the
 * dashboard build «площадка → отдел» from `#6`'s `managerUserIds` × THIS field, and
 * those filters must work for a МЕНЕДЖЕР session, which #25 (ADMIN) cannot serve.
 * Absent/`null` = no department; the buckets live in `../departments`.
 */
export interface UserDirectoryEntry {
  id: number;
  email: string;
  displayName: string | null;
  department?: ManagerDepartment | null;
}

const JSON_HEADERS = { 'Content-Type': 'application/json' };

export function venueLinksPath(venueId: number): string {
  return `/venues/${venueId}/links`;
}

export async function createVenueLink(venueId: number, body: VenueLinkCreate): Promise<VenueLinkDto> {
  const envelope = await requestEnvelope<VenueLinkDto>('POST', venueLinksPath(venueId), ['data'], {
    method: 'POST',
    headers: JSON_HEADERS,
    body: JSON.stringify(body),
  });
  return envelope.data;
}

export async function patchVenueLink(venueId: number, linkId: number, body: VenueLinkPatch): Promise<VenueLinkDto> {
  const envelope = await requestEnvelope<VenueLinkDto>('PATCH', `${venueLinksPath(venueId)}/${linkId}`, ['data'], {
    method: 'PATCH',
    headers: JSON_HEADERS,
    body: JSON.stringify(body),
  });
  return envelope.data;
}

export async function deleteVenueLink(venueId: number, linkId: number): Promise<void> {
  await requestEnvelope<undefined>('DELETE', `${venueLinksPath(venueId)}/${linkId}`, ['data'], { method: 'DELETE' });
}

/**
 * Optimistic helpers over the detail cache. `venueName` rides along so a
 * written row can be spliced into the `LinkDto[]` #7 answers (whose shape adds
 * the resolved `venueName` the write does not return).
 */
export type CachedLink = VenueLinkDto & { venueName: string | null };

export function cachedLinkOf(row: VenueLinkDto, venueName: string | null): CachedLink {
  return { ...row, venueName };
}

/** The optimistic write family: mutate the detail cache, roll back on a refusal. */
export function useCreateVenueLink(venueId: number, venueName: string | null) {
  const client = useQueryClient();
  const key = venueDetailQueryKey(venueId);

  return useMutation({
    mutationFn: (body: VenueLinkCreate) => createVenueLink(venueId, body),
    onMutate: async () => {
      await client.cancelQueries({ queryKey: key });
      const previous = client.getQueryData(key);
      return { previous };
    },
    onError: (_error, _body, context) => {
      if (context) client.setQueryData(key, context.previous);
    },
    onSuccess: (row) => {
      client.setQueryData(key, (old: { links?: CachedLink[] } | undefined) =>
        old
          ? {
              ...old,
              // The api may have RE-POINTED an existing row (the upsert matches
              // by url first), so the answer replaces whatever held that id.
              links: [...(old.links ?? []).filter((link) => link.id !== row.id), cachedLinkOf(row, venueName)],
            }
          : old,
      );
    },
    onSettled: () => {
      void client.invalidateQueries({ queryKey: key });
    },
  });
}

export function usePatchVenueLink(venueId: number) {
  const client = useQueryClient();
  const key = venueDetailQueryKey(venueId);

  return useMutation({
    mutationFn: ({ linkId, patch }: { linkId: number; patch: VenueLinkPatch }) => patchVenueLink(venueId, linkId, patch),
    onMutate: async ({ linkId, patch }) => {
      await client.cancelQueries({ queryKey: key });
      const previous = client.getQueryData(key);
      client.setQueryData(key, (old: { links?: CachedLink[] } | undefined) =>
        old
          ? {
              ...old,
              links: (old.links ?? []).map((link) =>
                link.id === linkId ? { ...link, ...patch, format: patch.format ?? null } : link,
              ),
            }
          : old,
      );
      return { previous };
    },
    onError: (_error, _variables, context) => {
      if (context) client.setQueryData(key, context.previous);
    },
    onSuccess: (row) => {
      // The server's row wins (it normalises the url, it owns the format).
      client.setQueryData(key, (old: { links?: CachedLink[] } | undefined) =>
        old ? { ...old, links: (old.links ?? []).map((link) => (link.id === row.id ? { ...link, ...row } : link)) } : old,
      );
    },
    onSettled: () => {
      void client.invalidateQueries({ queryKey: key });
    },
  });
}

export function useDeleteVenueLink(venueId: number) {
  const client = useQueryClient();
  const key = venueDetailQueryKey(venueId);

  return useMutation({
    mutationFn: (linkId: number) => deleteVenueLink(venueId, linkId),
    onMutate: async (linkId: number) => {
      await client.cancelQueries({ queryKey: key });
      const previous = client.getQueryData(key);
      client.setQueryData(key, (old: { links?: CachedLink[] } | undefined) =>
        old ? { ...old, links: (old.links ?? []).filter((link) => link.id !== linkId) } : old,
      );
      return { previous };
    },
    onError: (_error, _linkId, context) => {
      if (context) client.setQueryData(key, context.previous);
    },
    onSettled: () => {
      void client.invalidateQueries({ queryKey: key });
    },
  });
}

// ---------------------------------------------------------------------------
// #25.5 — the manager picker's directory (VIEWER)
// ---------------------------------------------------------------------------

export const USER_DIRECTORY_PATH = '/users/directory';

export function userDirectoryQueryKey(): readonly [string, string] {
  return ['users', 'directory'];
}

const NO_DIRECTORY: UserDirectoryEntry[] = [];

export async function fetchUserDirectory(signal?: AbortSignal): Promise<UserDirectoryEntry[]> {
  const envelope = await requestEnvelope<{ directory?: UserDirectoryEntry[] }>(
    'GET',
    USER_DIRECTORY_PATH,
    ['data', 'directory'],
    { signal },
  );
  return envelope.data?.directory ?? [];
}

/**
 * The picker's identities. One fetch per session worth of card views (15 min
 * stale), and a failure degrades to an empty list — the api answering 404 on an
 * older deployment must not break the card, only drop the names.
 */
export function useUserDirectory(enabled = true): UserDirectoryEntry[] {
  const query = useQuery({
    queryKey: userDirectoryQueryKey(),
    queryFn: ({ signal }) => fetchUserDirectory(signal),
    enabled,
    staleTime: 900_000,
    refetchOnWindowFocus: false,
    retry: 0,
  });
  return query.data ?? NO_DIRECTORY;
}
